import { NotificationStatuses } from './../../create-notification/create-notification/create-notification.component';
import { Component, Input, OnInit, Output, EventEmitter, inject, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { NotificationClient } from 'src/app/client/notification.client';
import { NotificationInstance, ReceivedNotification } from 'src/app/common/entity/notification/notification.model';
import { BroadcastService } from 'src/app/common/client/broadcast.service';
import { NotificationEvents } from 'src/app/common/entity/enum/notificationEvents';
import { Session } from 'src/app/common/entity/entities';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PermissionsCatalogueEnum } from '../../../entity/enum/permissionsCatalogue';
import { ScheduleAssistantContextService } from '../../chat-bot/schedule-assistant-context.service';

/** Where a TLM notification opens, by kind (FENIKS US-10.5, spec/Obavestenja.md — column "PDP"). */
type TlmTarget =
  | { area: 'time'; subTab: 'approvals' | 'activeShifts' | 'timecards' }
  | { area: 'schedule'; command: 'open_requests' | 'open_today' };

const TLM_TARGETS: { [kind: string]: TlmTarget } = {
  clockin_request:  { area: 'time', subTab: 'approvals' },
  missing_clockout: { area: 'time', subTab: 'activeShifts' },
  long_shift:       { area: 'time', subTab: 'activeShifts' },
  auto_clockout:    { area: 'time', subTab: 'timecards' },
  request_filed:    { area: 'schedule', command: 'open_requests' },
  bid_placed:       { area: 'schedule', command: 'open_requests' },
  no_show:          { area: 'schedule', command: 'open_today' },
  call_out:         { area: 'schedule', command: 'open_today' },
  not_confirmed:    { area: 'schedule', command: 'open_today' },
  standby:          { area: 'schedule', command: 'open_today' },
};

/** Manager TLM notification types: the FENIKS ones carry `kind`, the v1 ones imply it. */
const TLM_MANAGER_TYPES = new Set(['uclock_request', 'uclock_alarm']);
const TLM_V1_KINDS: { [typeCode: string]: string } = {
  uclock_approval_request: 'clockin_request',
  uclock_missing_clockout: 'missing_clockout',
  uclock_long_shift: 'long_shift',
};

@Component({
  selector: 'app-notification-overview',
  templateUrl: './notification-overview.component.html',
  styleUrls: ['./notification-overview.component.css']
})
export class NotificationOverviewComponent implements OnInit, OnDestroy {

  private notificationClient = inject(NotificationClient);
  private broadcastService = inject(BroadcastService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private scheduleAssistant = inject(ScheduleAssistantContextService);
  private destroy$ = new Subject<void>();

  @Input() showNotificationSidebar: boolean = false;
  @Output() sidebarClosed = new EventEmitter<boolean>();

  selectedNotificationIdForEmails: number | null = null; // Add this property
  showTicketPopup = false;
  showEmailNotificationsPopup = false;
  selectedNotificationForPopup: ReceivedNotification | null = null;
  activeNotificationId: number | null = null;
  showAddNotificationDialog: boolean = false;
  environment = environment;
  createNotificationsVisible: boolean = false;

  constructor() { }

  ngOnInit() {
    this.createNotificationsVisible =   (environment.portalCode == 'PAP')
    ? true
      : ((Session?.menu?.menuItems?.get('controlpanel')?.pages?.get('change_my_details')?.buttons?.get('create_notification')?.data?.IsVisible ?? false)
        && Session.userPermissions.find(up => up.FK_PermissionCatalogue == PermissionsCatalogueEnum.CreateNotifiactions && up.Level==0 /*visible*/));

    this.loadReceivedNotifications();
    if(this.createNotificationsVisible)
      this.loadUserNotifications();

    if (Session.valid && Session.userCode) {
      this.subscribeToNotifications();
    }
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToNotifications() {

    if (this.broadcastService.isConnected()) {
      this.setupNotificationSubscription();
    } else {
      this.broadcastService.init().then(() => {
        this.setupNotificationSubscription();
      }).catch((error) => {
        console.error('[NotificationOverviewComponent] Failed to initialize BroadcastService:', error);
      });
    }
  }

  onViewDetailsRequested(notification: ReceivedNotification, isCreatedByCurrentUser: boolean): void {
    if (notification.notificationType === 'support_ticket' && notification.ticketIdValue) {
      this.showNotificationSidebar = false;
      this.sidebarClosed.emit(false);
      this.router.navigate(['/help/support'], { queryParams: { ticketId: notification.ticketIdValue } });
      return;
    }
    if (this.isTlmManagerNotif(notification)) {
      this.showNotificationSidebar = false;
      this.sidebarClosed.emit(false);
      this.openTlmTarget(notification);
      return;
    }

    this.selectedNotificationForPopup = notification;
    this.selectedNotificationForPopup.isCreatedByCurrentUser = isCreatedByCurrentUser;
    this.activeNotificationId = notification.notificationInstance.PK_ID;
    this.showTicketPopup = true;
  }

  private isTlmManagerNotif(n: ReceivedNotification): boolean {
    return TLM_MANAGER_TYPES.has(n.notificationType) || !!TLM_V1_KINDS[n.notificationType];
  }

  private openTlmTarget(n: ReceivedNotification) {
    const kind = TLM_V1_KINDS[n.notificationType] ?? this.getNotificationFieldValue(n, 'kind') ?? '';
    const customerCode = this.getNotificationFieldValue(n, 'customer_code');
    const target = TLM_TARGETS[kind];

    if (target?.area === 'schedule' && customerCode) {
      this.scheduleAssistant.openBuilderAt(+customerCode, {
        kind: target.command,
        date: this.getNotificationFieldValue(n, 'date') ?? undefined,
      });
      return;
    }

    const queryParams: any = { _ts: Date.now() };
    if (target?.area === 'time') queryParams.subTab = target.subTab;
    if (customerCode) queryParams.customerCode = customerCode;
    // Auto clock-out: open THAT card — its business day (field date) and the card (ref_id).
    if (kind === 'auto_clockout') {
      const businessDate = this.getNotificationFieldValue(n, 'date');
      const timecardId = this.getNotificationFieldValue(n, 'ref_id');
      if (businessDate) queryParams.businessDate = businessDate;
      if (timecardId) queryParams.timecardId = timecardId;
    }
    this.router.navigate(['/dashboard/dailyStatus/dashtab_tlm'], { queryParams });
  }

  private getNotificationFieldValue(n: ReceivedNotification, fieldCode: string): string | null {
    const fields: any[] = n?.notificationInstance?.Fields ?? [];
    const field = fields.find(f => f?.Code === fieldCode);
    const value = field?.Value;
    return value !== undefined && value !== null && value !== '' ? String(value) : null;
  }


  handleViewConversation(notificationId: number): void {
    this.showTicketPopup = false;
    this.selectedNotificationIdForEmails = notificationId;
    this.showEmailNotificationsPopup = true;
  }

    closeTicketDetailsPopup(): void {
    this.showTicketPopup = false;
    this.selectedNotificationIdForEmails = null;
    this.activeNotificationId = null;
  }

  closeConversationPopup(): void {
    this.showEmailNotificationsPopup = false;
    this.activeNotificationId = null;
    this.selectedNotificationIdForEmails = null;
  }

  private setupNotificationSubscription() {

    this.broadcastService
      .subscribe({
        customerCode: null,
        event: NotificationEvents.GSNotification,
        requestId: Session.userCode.toString(),
        expiresInSeconds: 28800 // 8 hours
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (message) => {
          this.handleIncomingNotification(message);
        },
        error: (error) => {
          console.error('[NotificationOverviewComponent] Error in notification subscription:', error);
        }
      });

  }

  private handleIncomingNotification(signalRMessage: any) {
    try {

      let notificationData;
      if (typeof signalRMessage.Message === 'string') {
        notificationData = JSON.parse(signalRMessage.Message);
      } else {
        notificationData = signalRMessage.Message;
      }

      if(notificationData?.NotifyCreatedBy){
        let notificationId = notificationData?.NotificationId;

        if(Session.userCreatedNotifications && Session.userCreatedNotifications.length > 0){
          let index = Session.userCreatedNotifications.findIndex(n => n.PK_ID === notificationId);
          if(index > -1){
            Session.userCreatedNotifications = Session.userCreatedNotifications.map(n => {
              if(n.PK_ID === notificationId){
                n.NotificationStatusId = NotificationStatuses.Published;
                n.NotificationStatusDescription = 'Published';
                if(notificationData.PublishedAt){
                  n.PublishedAt = notificationData.PublishedAt;
                }
              }
              return n;
            }).sort((a, b) => {
              const aDate = new Date(a.PublishedAt ?? a.CreatedAt).getTime();
              const bDate = new Date(b.PublishedAt ?? b.CreatedAt).getTime();
              return bDate - aDate;
            });

            Session.notificationsUpdated$.next();
          }
        }
      }

      if (notificationData?.NotificationId) {
        const jsonBody = JSON.stringify(notificationData, null, 2);

        console.log('Notification body: ',jsonBody);
        // this.showNotificationToast(
        //   notificationData.Subject,
        //   jsonBody,
        //   notificationData.NotificationId
        // );
      }

      if (notificationData?.NotificationId && !notificationData.Fields) {
        this.fetchAndUpdateNotification(notificationData.NotificationId);
      }
      else if (notificationData.Fields) {
        this.updateSessionNotifications(notificationData);
      }
    } catch (error) {
      console.error('[NotificationOverviewComponent] Error handling incoming notification:', error);
    }
  }

  private showNotificationToast(subject: string, jsonBody: string, notificationId: number) {

    const toastId = `notification_${notificationId}_${Date.now()}`;

    const displayBody = jsonBody.length > 300
      ? jsonBody.substring(0, 300) + '...\n\n[Click to view full details]'
      : jsonBody;

    this.messageService.add({
      id: toastId,
      severity: 'info',
      summary: subject,
      detail: displayBody,
      life: 8000,
      sticky: false,
      data: {
        notificationId: notificationId,
        isNotificationToast: true,
        fullJsonBody: jsonBody
      }
    });

  }

  private fetchAndUpdateNotification(notificationId: number) {
    this.notificationClient.getNotificationsForUser(notificationId).then(
      (response) => {
        if (response && response.result && response.result.length > 0) {
          const notification = response.result[0];
          this.updateSessionNotifications(notification);
        }
        else {
          // Notification was deleted on the server, remove it from the session
          this.removeNotificationFromSession(notificationId);
        }
      },
      (error) => {
        console.error('[NotificationOverviewComponent] Failed to fetch notification details:', error);
      }
    );
  }

  private removeNotificationFromSession(notificationId: number): void {
    let s = Session;
    const index = Session.userNotifications.findIndex(
      n => n.PK_ID === notificationId
    );

    if (index > -1) {
      Session.userNotifications.splice(index, 1);
      Session.notificationsUpdated$.next();

    }
  }

  private updateSessionNotifications(notification: any) {
    // Check if notification already exists
    const existingIndex = Session.userNotifications.findIndex(
      n => n.PK_ID === notification.PK_ID
    );

    if (existingIndex > -1) {
      // Update existing notification
      Session.userNotifications[existingIndex] = notification;
    } else {
      // Add new notification at the beginning
      Session.userNotifications.unshift(notification);
    }

    // Trigger update for components
    Session.notificationsUpdated$.next();
  }

  //#endregion

  //#region UI Methods


  private loadReceivedNotifications(): void {
    if (Session.userNotifications && Session.userNotifications.length > 0) {
      console.log('Received notifications already in Session');
    } else {
      // Fetch from API if not in session
      this.fetchReceivedNotificationsFromApi();
    }
  }

  private loadUserNotifications(): void {
    if (Session.userCreatedNotifications && Session.userCreatedNotifications.length > 0) {
      console.log('User created notifications already in Session');
    } else {
      // Fetch from API if not in session
      this.fetchUserNotificationsFromApi();
    }
  }


  private fetchReceivedNotificationsFromApi(): void {
    this.notificationClient.getNotificationsForUser().then(
      (response) => {
        if (response && response.result) {
          Session.userNotifications = response.result;
          Session.notificationsUpdated$.next();
        }
      },
      (error) => {
        console.error('[NotificationOverviewComponent] Failed to load received notifications from API:', error);
      }
    );
  }

  private fetchUserNotificationsFromApi(): void {
    this.notificationClient.getUserNotifications().then(
      (response) => {
        if (response && response.result) {
          Session.userCreatedNotifications = response.result;
        }
      },
      (error) => {
        console.error('[NotificationOverviewComponent] Failed to load user created notifications from API:', error);
      }
    );
  }

  onSidebarClosed() {
    this.sidebarClosed.emit(true);
  }

  onToogleAddNotification(closeDialog?: boolean) {
    if (closeDialog)
      this.showAddNotificationDialog = false;
    else
      this.showAddNotificationDialog = !this.showAddNotificationDialog;
  }

  onNotificationCreated(notification: NotificationInstance) {
    this.showAddNotificationDialog = false;
    Session.userCreatedNotifications.unshift(notification as any);
  }

  //#endregion
}
